import { useState } from "react";
import { format } from "date-fns";
import { ChevronDown, ChevronRight, type LucideIcon } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

export type ActivityEntry = {
  key: string;
  label: string;
  at: string | Date;
  Icon: LucideIcon;
  accent?: string;
};

export function ActivityTimeline({
  entries,
  defaultOpen = false,
}: {
  entries: ActivityEntry[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  if (entries.length === 0) {
    return null;
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex min-h-9 items-center gap-1.5 rounded-lg px-1 text-xs font-medium text-slate-600 transition-colors hover:bg-primary/5 hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" aria-hidden />
          )}
          Activity
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-1.5 space-y-2 border-l border-slate-200 pl-3">
          {entries.map(({ key, label, at, Icon, accent }) => (
            <li
              key={key}
              className={cn(
                "flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs leading-5 text-slate-500 [overflow-wrap:anywhere]",
                accent,
              )}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{label}</span>
              <span className="text-slate-400">
                {format(new Date(at), "MMM d, h:mm a")}
              </span>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}
